import { useMemo, useState } from 'react';
import ReceivablesTableView from './ReceivablesTableView';
import PayablesTableView from './PayablesTableView';
import dealQueries from '../../../../queries/dealQueries';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import { useQuery } from 'react-query';
import { CommissionStatus, DealCategory, newRoles } from '../../../../utils/envConfig';
import { addMilliseconds } from 'date-fns';
import { getTimeUntil } from '../../../../utils/Utils';
import { useSelector } from 'react-redux';

const DEFAULT_PAGE_SIZE = 5;
const FIFTEEN_MINUTES = 15 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;
const TAX_RATE = 0.05; // 5% tax rate

// Utility function to calculate the total commission including tax
const calculateTotalCommission = (deal) => {
  const salePrice = deal?.agreedSalePrice || 0;
  const estimatedPrice = deal?.estimatedPrice || 0;
  let baseCommission = 0;

  // Check if deal has agreedCommission (as percentage)
  if (deal?.agreedCommission) {
    // Calculate commission amount based on percentage of sale price
    baseCommission = (deal.agreedCommission / 100) * salePrice;
  } else {
    // Sum of buyer and seller agreed commissions (as percentages)
    const buyerCommissionPercent = deal?.buyerAgreedCommission || 0;
    const sellerCommissionPercent = deal?.sellerAgreedCommission || 0;

    // Convert percentages to amounts based on sale price
    const buyerCommissionAmount = (buyerCommissionPercent / 100) * salePrice;
    const sellerCommissionAmount = (sellerCommissionPercent / 100) * salePrice;

    baseCommission = buyerCommissionAmount + sellerCommissionAmount;
  }

  // Calculate tax
  const taxAmount = baseCommission * TAX_RATE;

  // Total commission with tax
  return {
    baseCommission,
    taxAmount,
    totalCommission: baseCommission + taxAmount,
    commissionPercentage:
      deal?.agreedCommission || (deal?.buyerAgreedCommission || 0) + (deal?.sellerAgreedCommission || 0),
    isTwoPartCommission: !!deal?.buyerAgreedCommission && !!deal?.sellerAgreedCommission,
    buyerAgreedCommission: deal?.buyerAgreedCommission || 0,
    sellerAgreedCommission: deal?.sellerAgreedCommission || 0,
    projectedRevenue: deal?.projectedRevenue || 0,
    projectedDate: deal?.projectedDate || null,
    salePrice,
    estimatedPrice
  };
};

// Utility function to calculate remaining commission
const calculateRemainingCommission = (deal, receivablesData, payablesData) => {
  // Get total commission
  const {
    baseCommission,
    taxAmount,
    totalCommission,
    commissionPercentage,
    salePrice,
    isTwoPartCommission,
    buyerAgreedCommission,
    sellerAgreedCommission,
    estimatedPrice,
    projectedRevenue,
    projectedDate
  } = calculateTotalCommission(deal);

  // Calculate received amount from receivables
  let receivedAmount = 0;
  if (receivablesData?.data && Array.isArray(receivablesData.data)) {
    receivedAmount = receivablesData.data.reduce((sum, transaction) => sum + (transaction.amount || 0), 0);
  }

  // Calculate paid amount from payables
  let paidAmount = 0;
  if (payablesData?.data && Array.isArray(payablesData.data)) {
    paidAmount = payablesData.data.reduce((sum, transaction) => sum + (transaction.amount || 0), 0);
  }

  // Calculate remaining amount
  const remainingAmount = totalCommission - receivedAmount;

  return {
    baseCommission,
    taxAmount,
    totalCommission,
    receivedAmount,
    paidAmount,
    remainingAmount,
    commissionPercentage,
    salePrice,
    estimatedPrice,
    isTwoPartCommission,
    buyerAgreedCommission,
    sellerAgreedCommission,
    projectedRevenue,
    projectedDate
  };
};

const DealTransactionsContainer = ({ deal, refetchFn }) => {
  const axios = useAxiosPrivate();

  const { dealId } = useMemo(
    () => ({
      dealId: deal?.id ?? deal?._id
    }),
    [deal]
  );

  const isEoi = deal?.category === DealCategory.EOI;
  const isPendingPayment = deal?.commissionStatus !== CommissionStatus.FullyPaid;

  /**
   * Separate state for receivables query parameters.
   * @type {[import('./types').TransactionsQueryState, import("react").Dispatch<import("react").SetStateAction<import('./types').TransactionsQueryState>>]}
   */
  const [receivablesQueryParams, setReceivablesQueryParams] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    paymentMethod: null,
    payeeType: null,
    fromAmount: null,
    toAmount: null,
    fromBalance: null,
    toBalance: null,
    fromDate: null,
    toDate: null,
    fromPaymentDate: null,
    toPaymentDate: null,
    isReceivable: true
  });

  /**
   * Separate state for payables query parameters.
   * @type {[import('./types').TransactionsQueryState, import("react").Dispatch<import("react").SetStateAction<import('./types').TransactionsQueryState>>]}
   */
  const [payablesQueryParams, setPayablesQueryParams] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    paymentMethod: null,
    payeeType: null,
    fromAmount: null,
    toAmount: null,
    fromBalance: null,
    toBalance: null,
    fromDate: null,
    toDate: null,
    fromPaymentDate: null,
    toPaymentDate: null,
    isReceivable: false
  });

  // Query for receivables
  const { queryKey: receivablesQueryKey, queryFn: fetchReceivables } = dealQueries.Transactions.getByDeal(
    axios,
    dealId,
    {
      params: { ...receivablesQueryParams }
    }
  );

  const {
    data: receivablesData,
    isLoading: isReceivablesLoading,
    error: receivablesError
  } = useQuery(receivablesQueryKey, fetchReceivables, {
    enabled: !!dealId,
    staleTime: FIFTEEN_MINUTES,
    refetchOnWindowFocus: false,
    refetchInterval: THIRTY_MINUTES
  });

  // Query for payables
  const { queryKey: payablesQueryKey, queryFn: fetchPayables } = dealQueries.Transactions.getByDeal(axios, dealId, {
    params: { ...payablesQueryParams }
  });

  const {
    data: payablesData,
    isLoading: isPayablesLoading,
    error: payablesError
  } = useQuery(payablesQueryKey, fetchPayables, {
    enabled: !!dealId,
    staleTime: FIFTEEN_MINUTES,
    refetchOnWindowFocus: false,
    refetchInterval: THIRTY_MINUTES
  });

  // State for modal management
  const [isReceivableModalOpen, setIsReceivableModalOpen] = useState(false);
  const [isPayableModalOpen, setIsPayableModalOpen] = useState(false);

  // Toggle functions for modals
  const toggleReceivableModal = () => setIsReceivableModalOpen(!isReceivableModalOpen);
  const togglePayableModal = () => setIsPayableModalOpen(!isPayableModalOpen);

  // Calculate commission details
  const commissionDetails = useMemo(() => {
    return calculateRemainingCommission(deal, receivablesData, payablesData);
  }, [deal, receivablesData, payablesData]);

  const commissionPercentage = useMemo(() => {
    let commission = '';

    if (commissionDetails.isTwoPartCommission) {
      commission = `${commissionDetails.commissionPercentage?.toFixed(
        2
      )}% (Buyer: ${commissionDetails.buyerAgreedCommission?.toFixed(
        2
      )}%, Seller: ${commissionDetails.sellerAgreedCommission?.toFixed(2)}%)`;
    } else {
      commission = `${commissionDetails.commissionPercentage?.toFixed(2)}%`;
    }

    return commission;
  }, [commissionDetails]);

  const authUser = useSelector((state) => state?.user?.loggedInUser);
  const isAuthChannelPartnerManager = authUser?.role && [newRoles.ChannelPartnerManager].includes(authUser?.role);

  if (isAuthChannelPartnerManager) {
    //! HIDE FROM CHANNEL PARTNER MANAGER
    return null;
  }

  return (
    <>
      {/* Commission Summary */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="card-title mb-0">Commission Summary</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="text-muted">{isEoi ? 'Estimated Price' : 'Sale Price:'}</label>
              <p className="fw-medium">
                AED {isEoi ? commissionDetails.estimatedPrice?.toFixed(2) : commissionDetails.salePrice?.toFixed(2)}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted">Commission Rate:</label>
              <p className="fw-medium">{commissionPercentage}</p>
            </div>
            {isPendingPayment ? (
              <>
                <div className="col-md-6 mb-3">
                  <label className="text-muted">Projected Revenue:</label>
                  <p className="fw-medium">AED {commissionDetails.projectedRevenue?.toFixed(2)}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted">Projected Date:</label>
                  <p className="fw-medium">{commissionDetails.projectedDate ? getTimeUntil(addMilliseconds(new Date(), (new Date(commissionDetails.projectedDate)) - Date.now())) : 'N/A'}</p>
                </div>
              </>
            ) : null}
            <div className="col-md-6 mb-3">
              <label className="text-muted">Base Commission:</label>
              <p className="fw-medium">AED {commissionDetails.baseCommission?.toFixed(2)}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted">Tax (5%):</label>
              <p className="fw-medium">AED {commissionDetails.taxAmount?.toFixed(2)}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted">Total Commission:</label>
              <p className="fw-medium">AED {commissionDetails.totalCommission?.toFixed(2)}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted">Already Received:</label>
              <p className="fw-medium">AED {commissionDetails.receivedAmount?.toFixed(2)}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="text-muted">Already Paid Out:</label>
              <p className="fw-medium">AED {commissionDetails.paidAmount?.toFixed(2)}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="fw-bold">Remaining Commission:</label>
              <p className="fs-5 fw-bold text-primary">AED {commissionDetails.remainingAmount?.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Receivables Table */}
      <ReceivablesTableView
        title="Receivables"
        transactionsState={{
          transactions: receivablesData?.data,
          totalTransactions: receivablesData?.found,
          isLoading: isReceivablesLoading,
          error: receivablesError
        }}
        deal={deal}
        queryParams={receivablesQueryParams}
        setQueryParams={setReceivablesQueryParams}
        refetchFn={refetchFn}
        isModalOpen={isReceivableModalOpen}
        toggleModal={toggleReceivableModal}
        modalId="receivable-modal"
      />

      {/* Payables Table */}
      <PayablesTableView
        title="Payables"
        transactionsState={{
          transactions: payablesData?.data,
          totalTransactions: payablesData?.found,
          isLoading: isPayablesLoading,
          error: payablesError
        }}
        deal={deal}
        queryParams={payablesQueryParams}
        setQueryParams={setPayablesQueryParams}
        refetchFn={refetchFn}
        isModalOpen={isPayableModalOpen}
        toggleModal={togglePayableModal}
        modalId="payable-modal"
        remainingPayableAmount={commissionDetails.receivedAmount - commissionDetails.paidAmount}
      />
    </>
  );
};

export default DealTransactionsContainer;
